import { Alert, ScrollArea, Flex, Text, Table, Button, Card, Divider } from "@mantine/core";
import { useState, useEffect } from "react";
import { createStyles } from "@mantine/emotion";
import {
  expiryIsoFromSelection,
  expirySelectionFromIso,
  formatDate,
  getRole,
  isLicenseExpirySelectionIncomplete,
  LicenseExpirySelection,
} from "../../utils/CommonUtil";
import LicenseExpirySelect from "../common/LicenseExpirySelect";
import {
  approveOfflineLicenseRequest,
  deleteOfflineLicenseRequest,
  getOfflineLicenseRequests,
} from "../../utils/api-communication/RestApiCli";
import { modals } from "@mantine/modals";
import { MODAL_POPOVER_Z_INDEX, MODAL_Z_INDEX } from "@constants/zIndex";

const useStyles = createStyles((_theme) => ({
  table: {
    "& td:nth-child(2)": {
      background: "transparent",
    },
    cursor: "pointer",
  },

  scrollArea: {
    maxHeight: 200,
    overflowY: "auto",
  },
}));

const APPROVE_REQUEST_MODAL_ID = "approve-offline-license-request";

export function approvalErrorMessage(error: any): string {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (Array.isArray(data?.message)) return data.message.join(", ");
  if (typeof data?.message === "string") return data.message;
  return "The offline license could not be approved. Please try again.";
}

function ApproveRequestModalBody({
  requestedExpiresAt,
  onApprove,
}: {
  requestedExpiresAt?: string | null;
  onApprove: (expiresAt: string | null, done: (errorMessage: string) => void) => void;
}) {
  const [expiresAt, setExpiresAt] = useState<LicenseExpirySelection>(
    expirySelectionFromIso(requestedExpiresAt),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  return (
    <>
      <LicenseExpirySelect
        description={
          requestedExpiresAt ? `Requested: ${formatDate(requestedExpiresAt)}` : undefined
        }
        value={expiresAt}
        onChange={setExpiresAt}
        dropdownZIndex={MODAL_POPOVER_Z_INDEX}
      />
      {error && (
        <Alert color="red" variant="light" mt="md">
          {error}
        </Alert>
      )}
      <Button
        fullWidth
        mt="md"
        loading={submitting}
        disabled={isLicenseExpirySelectionIncomplete(expiresAt)}
        onClick={() => {
          setSubmitting(true);
          setError("");
          onApprove(expiryIsoFromSelection(expiresAt), (errorMessage) => {
            setSubmitting(false);
            setError(errorMessage);
          });
        }}
      >
        Submit
      </Button>
    </>
  );
}

export default function OfflineLicenseRequestList(props) {
  const [offlineLicenseRequests, setOfflineLicenseRequests] = useState([]);
  const [role] = useState(getRole());
  const { classes } = useStyles();

  const updateLists = () => {
    getOfflineLicenseRequests(
      (res) => {
        setOfflineLicenseRequests(res.data);
      },
      (error) => {
        console.error(error);
      },
    );
  };

  useEffect(() => {
    updateLists();
  }, [props.changed]);

  // modals.open is imperative and outlives this component, so navigating away with the
  // dialog open would otherwise leave it on top of the next route.
  useEffect(() => () => modals.close(APPROVE_REQUEST_MODAL_ID), []);

  const handleDeleteRequest = (id) => {
    deleteOfflineLicenseRequest(
      { id: id },
      (res) => {
        console.log(res);
        updateLists();
      },
      (error) => {
        console.error(error);
      },
    );
  };

  const handleAcceptRequest = (item) => {
    modals.open({
      modalId: APPROVE_REQUEST_MODAL_ID,
      zIndex: MODAL_Z_INDEX,
      title: "Approve Offline License Request",
      children: (
        <ApproveRequestModalBody
          requestedExpiresAt={item.expiresAt}
          onApprove={(expiresAt, done) =>
            approveOfflineLicenseRequest(
              { id: item._id, ...(expiresAt ? { expiresAt } : {}) },
              () => {
                modals.close(APPROVE_REQUEST_MODAL_ID);
                updateLists();
              },
              (error) => {
                console.error(error);
                done(approvalErrorMessage(error));
              },
            )
          }
        />
      ),
    });
  };

  return (
    offlineLicenseRequests.length > 0 && (
      <>
        <Card mt={"sm"} p={"sm"} style={{ background: "var(--oculera-surface-alt)" }}>
          <Text> Offline License Requests</Text>
          <ScrollArea.Autosize mah={300}>
            <Table
              className={classes.table}
              style={{ background: "none" }}
              highlightOnHover
              striped
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Organization</Table.Th>
                  {(role === "admin" || role === "manager") && <Table.Th>Distributor</Table.Th>}
                  <Table.Th>Username</Table.Th>
                  <Table.Th>Emails</Table.Th>
                  <Table.Th>Expiry</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody className={classes.scrollArea}>
                {offlineLicenseRequests.map((item, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>{item.orgId}</Table.Td>
                    {(role === "admin" || role === "manager") && <Table.Td>{item.distId}</Table.Td>}
                    <Table.Td>{item.username}</Table.Td>
                    <Table.Td>{item.emails.join(", ")}</Table.Td>
                    <Table.Td>
                      {item.expiresAt ? formatDate(item.expiresAt) : "1 year from issue"}
                    </Table.Td>
                    <Table.Td>
                      {role === "admin" || role === "manager" ? (
                        <Flex>
                          <Button
                            onClick={() => handleAcceptRequest(item)}
                            mr={"xs"}
                            color="green"
                            size="compact-xs"
                            variant="outline"
                          >
                            <Text size={"xs"}>Accept</Text>
                          </Button>
                          <Button
                            onClick={() => handleDeleteRequest(item._id)}
                            color="red"
                            variant="outline"
                            size="compact-xs"
                          >
                            <Text size={"xs"}>Reject</Text>
                          </Button>
                        </Flex>
                      ) : null}
                      {role === "distributor" ? (
                        <Flex>
                          <Button
                            onClick={() => handleDeleteRequest(item._id)}
                            color="red"
                            variant="outline"
                            size="compact-xs"
                          >
                            <Text size={"xs"}>Cancel</Text>
                          </Button>
                        </Flex>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </Card>
        <Divider size={"sm"} mt={"sm"} mb={"sm"} />
      </>
    )
  );
}
